import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { auctioneerDataTemplatePropertiesType } from '../../types';
import useExtendedState from '../../hooks/useExtendedState';
import ExpandedListTab from '../ExpandedListTab';
import {
    AUCTIONEER_DATA_TEMPLATE_NAME_MAX_LETTERS,
    auctioneerDataTemplateItemsValidator,
    auctioneerDataTemplateNameValidator,
} from '../../validators/auctioneerDataTemplates';
import TextField from '../fields/TextField';
import useMultiValidator from '../../hooks/useMultiValidator';
import SaveButton from '../buttons/SaveButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_AUCTIONEER_DATA_TEMPLATES_QUERY_NAME,
    CREATE_AUCTIONEER_DATA_TEMPLATE,
} from '../../graphql/auctioneerDataTemplates';
import AuctioneerDataMatrix from '../auctioneer-data-matrix/AuctioneerDataMatrix';
import Constants from '../../utils/Constants';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctioneerDataTemplateCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    auctioneerDataTemplateProps: auctioneerDataTemplatePropertiesType.isRequired,
};

function AuctioneerDataTemplateCreateTab(props) {
    const {
        hideHandler,
        auctioneerDataTemplateProps,
    } = props;
    const {
        availableAuctioneers,
        availableCountries,
        maxQuantitySum,
    } = auctioneerDataTemplateProps;
    const { t } = useTranslation();
    const nameState = useExtendedState('');
    const templateItemsState = useExtendedState([]);
    const { validators, isValid } = useMultiValidator(false, {
        name: compositeValidator(auctioneerDataTemplateNameValidator(t), wafValidator(t)),
        templateItems: auctioneerDataTemplateItemsValidator(t, Constants.MIN_AUCTIONEER_DATA_QUANTITY, maxQuantitySum),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_AUCTIONEER_DATA_TEMPLATE, {
        variables: {
            name: nameState.value,
            templateItems: templateItemsState.value.map((templateItem) => ({
                memberId: templateItem.auctioneer.memberId,
                countryCode: templateItem.country.code,
                quantity: templateItem.quantity,
            })),
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-create-success', dataProperty: 'createAuctioneerDataTemplate' },
        failureMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-create-failure', params: { name: nameState.value } },
        refetchQueries: [ALL_AUCTIONEER_DATA_TEMPLATES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="auctioneer-data-template-create-tab"
            title={t('auctioneer-data-templates.auctioneer-data-template-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('auctioneer-data-templates.name')}
                    state={nameState}
                    validator={validators.name}
                    maxLength={AUCTIONEER_DATA_TEMPLATE_NAME_MAX_LETTERS}
                    className="auctioneer-data-template-create-tab--fields--name wide"
                    placeholder={t('auctioneer-data-templates.placeholder.name')}
                    isRequired
                />
            </div>
            <div className="auctioneer-data-template-create-tab--auctioneer-data-template-matrix">
                <AuctioneerDataMatrix
                    availableAuctioneers={availableAuctioneers}
                    availableCountries={availableCountries}
                    maxQuantitySum={maxQuantitySum}
                    items={templateItemsState.value}
                    validator={validators.templateItems}
                    onItemsChange={templateItemsState.setValue}
                    onReset={templateItemsState.reset}
                    isChanged={templateItemsState.isChanged}
                    isRequired
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('auctioneer-data-templates.confirm-create-auctioneer-data-template', { name: nameState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default AuctioneerDataTemplateCreateTab;
